import { useClientPortalAPI, useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useCanUpdateGroups } from "../../../../components/permissions/Permissions";
import { UserPicker } from "../../../../components/user-picker/UserPicker";
import { Schemas } from "../../../../types";
import { UserRemoveModal } from "../../components/modals/UserRemoveModal";

export type GroupUser = Schemas["CustomerAccountUsersListCompact"];

export function GroupUsersTab() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const [deleteUser, setDeleteUser] = useState<GroupUser>();

  const { submit } = useSubmit(
    `/api/internal/client-portal/customer-groups/${groupId}/users/update/`,
    { throwErrors: true }
  );

  const {
    data: group,
    isLoading: isGroupLoading,
    mutate: mutateGroup,
  } = useClientPortalAPI(
    `/api/internal/client-portal/customer-groups/${groupId}/`
  );

  const isReadOnly = !useCanUpdateGroups() || !group?.is_active;
  const userSet = group?.user_set ?? [];
  const selectedKeys = userSet?.map((user) => user.id);

  const handleRemoveUser = async () => {
    const users = userSet
      ?.filter((user: GroupUser) => user.id !== deleteUser?.id)
      .map((user: GroupUser) => user.id);

    try {
      await handleSubmit(users);
    } finally {
      handleClose();
    }
  };

  const handleClose = () => {
    setDeleteUser(undefined);
  };

  const handleSubmit = async (values: string[]) => {
    try {
      await submit({ users: [...values] });
      mutateGroup();
      showToast(t("Group updated successfully"), { type: "success" });
    } catch (e) {
      console.error(e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
    }
  };

  return (
    <>
      <UserPicker
        label={t("Add members")}
        selectedKeys={selectedKeys}
        onChange={handleSubmit}
        onRemove={(userId) =>
          setDeleteUser(userSet.find(({ id }) => id === userId))
        }
        isLoading={isGroupLoading}
        isDisabled={isReadOnly}
      />
      {deleteUser && (
        <UserRemoveModal
          handleClose={handleClose}
          user={deleteUser}
          groupName={group?.name}
          handleRemoveUser={handleRemoveUser}
        />
      )}
    </>
  );
}
